import { CallIcon, EmailIcon, MapIcon } from "@/assets/icons";
import {
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  TwitterIcon,
} from "@/assets/icons/social";
import Link from "next/link";
import AccountLinks from "./AccountLinks";
import FooterBottom from "./FooterBottom";
import { AppStoreIcon, GooglePlayIcon } from "./icons";
import QuickLinks from "./QuickLinks";
import { useEcommerce } from "../EcommerceProvider/EcommerceProvider";
import { formatAddress, getLegalFormLabel } from "@/utils/company";
import { useTranslations } from "next-intl";

const Footer = () => {
  const ecommerce = useEcommerce();
  const t = useTranslations("Footer");
  const tContact = useTranslations("Contact");

  const companyName =
    ecommerce.companyInfo?.companyName || ecommerce.name || "Sklep";

  const normalizedCompanyName =
    companyName.charAt(0).toUpperCase() + companyName.slice(1).toLowerCase();

  const addressText =
    formatAddress(ecommerce.companyAddress) ||
    formatAddress(ecommerce.correspondenceAddress);

  const phoneNumberHidden = ecommerce.contact?.phoneNumberHidden ?? false;

  const phoneText = !phoneNumberHidden
    ? [ecommerce.contact?.phonePrefix, ecommerce.contact?.phoneNumber]
        .filter(Boolean)
        .join(" ")
        .trim()
    : null;

  const phoneHref =
    !phoneNumberHidden && ecommerce.contact?.phoneNumber
      ? `tel:${(ecommerce.contact.phonePrefix || "") + ecommerce.contact.phoneNumber}`.replace(
          /\s+/g,
          ""
        )
      : null;
  const otherPhones = ecommerce.contact?.otherPhonesNumbers ?? [];
  const phonePrefix = (ecommerce.contact?.phonePrefix ?? "").trim();

  const email = ecommerce.contact?.email || null;

  const social = ecommerce.socialLinks;

  return (
    <footer className="overflow-hidden">
      <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 xl:px-0">
        {/* <!-- footer menu start --> */}
        <div className="flex flex-wrap xl:flex-nowrap gap-10 xl:gap-19 xl:justify-between pt-17.5 xl:pt-22.5 pb-10 xl:pb-15">
          <div className="max-w-[330px] w-full">
            <h2 className="mb-7.5 text-custom-1 font-medium text-dark">
              {normalizedCompanyName}
            </h2>

            <ul className="flex flex-col gap-3">
              {addressText && (
                <li className="flex gap-3.5">
                  <span className="shrink-0">
                    <MapIcon className="w-6 h-6 text-blue" />
                  </span>
                  {addressText}
                </li>
              )}

              {(phoneHref && phoneText || otherPhones.length > 0) && (
                <li className="flex flex-col gap-1.5">
                  {phoneHref && phoneText && (
                    <Link href={phoneHref} className="flex items-center gap-3.5">
                      <CallIcon className="w-6 h-6 text-blue" />
                      {tContact("phoneLabel")}: {phoneText}
                    </Link>
                  )}

                  {otherPhones.map((phone, idx) =>
                    phone.phoneNumber
                      ? (() => {
                          const fullPhone = [phonePrefix, phone.phoneNumber]
                            .filter(Boolean)
                            .join(" ")
                            .trim();
                          const showIcon = idx === 0 && !phoneHref;

                          return showIcon ? (
                            <Link
                              key={idx}
                              href={`tel:${fullPhone.replace(/\s+/g, "")}`}
                              className="flex items-center gap-3.5"
                            >
                              <CallIcon className="w-6 h-6 text-blue" />
                              {phone.name ? `${phone.name}: ` : ""}
                              {fullPhone}
                            </Link>
                          ) : (
                            <Link
                              key={idx}
                              href={`tel:${fullPhone.replace(/\s+/g, "")}`}
                              className="ml-10"
                            >
                              {phone.name ? `${phone.name}: ` : ""}
                              {fullPhone}
                            </Link>
                          );
                        })()
                      : null
                  )}
                </li>
              )}

              {email && (
                <li>
                  <Link
                    href={`mailto:${email}`}
                    className="flex items-center gap-3.5"
                  >
                    <EmailIcon className="w-6 h-6 text-blue" />
                    {email}
                  </Link>
                </li>
              )}
            </ul>

            {/* Company Info */}
            {ecommerce.companyInfo && (
              <div className="mt-6 text-sm text-gray-600">
                {ecommerce.companyInfo.legalForm && (
                  <p>{getLegalFormLabel(ecommerce.companyInfo.legalForm)}</p>
                )}
                {ecommerce.companyInfo.nip && (
                  <p>NIP: {ecommerce.companyInfo.nip}</p>
                )}
                {ecommerce.companyInfo.regon && (
                  <p>REGON: {ecommerce.companyInfo.regon}</p>
                )}
                {ecommerce.companyInfo.krs && (
                  <p>KRS: {ecommerce.companyInfo.krs}</p>
                )}
              </div>
            )}

            {/* <!-- Social Links start --> */}
            {(social?.facebook ||
              social?.twitter ||
              social?.instagram ||
              social?.linkedin) && (
              <div className="flex items-center gap-4 mt-7.5">
                {social?.facebook && (
                  <Link
                    href={social.facebook}
                    className="flex duration-200 ease-out hover:text-blue"
                  >
                    <span className="sr-only">{t("facebookLink")}</span>
                    <FacebookIcon />
                  </Link>
                )}

                {social?.twitter && (
                  <Link
                    href={social.twitter}
                    className="flex duration-200 ease-out hover:text-blue"
                  >
                    <span className="sr-only">{t("twitterLink")}</span>
                    <TwitterIcon />
                  </Link>
                )}

                {social?.instagram && (
                  <Link
                    href={social.instagram}
                    className="flex duration-200 ease-out hover:text-blue"
                  >
                    <span className="sr-only">{t("instagramLink")}</span>
                    <InstagramIcon />
                  </Link>
                )}

                {social?.linkedin && (
                  <Link
                    href={social.linkedin}
                    aria-label={t("linkedinLink")}
                    className="flex duration-200 ease-out hover:text-blue"
                  >
                    <span className="sr-only">{t("linkedinLink")}</span>
                    <LinkedInIcon />
                  </Link>
                )}
              </div>
            )}
            {/* <!-- Social Links end --> */}
          </div>

          <AccountLinks />

          <QuickLinks />

          {/*           <div className="w-full sm:w-auto">
            <h2 className="mb-7.5 text-custom-1 font-medium text-dark lg:text-right">
              {t("downloadApp")}
            </h2>

            <p className="mb-4 lg:text-right text-custom-sm">
              {t("downloadPromo")}
            </p>

            <ul className="flex flex-col gap-3 lg:items-end">
              <li>
                <Link
                  className="inline-flex items-center gap-3 py-[9px] pl-4 pr-7.5 text-white rounded-xl bg-dark ease-out duration-200 hover:bg-opacity-95"
                  href="#"
                >
                  <AppStoreIcon />

                  <div>
                    <span className="block text-custom-xs">
                      {t("downloadOn")}
                    </span>
                    <p className="font-medium">{t("appStore")}</p>
                  </div>
                </Link>
              </li>

              <li>
                <Link
                  className="inline-flex items-center gap-3 py-[9px] pl-4 pr-8.5 text-white rounded-xl bg-blue ease-out duration-200 hover:bg-opacity-95"
                  href="#"
                >
                  <GooglePlayIcon />

                  <div>
                    <span className="block text-custom-xs">{t("getItOn")}</span>
                    <p className="font-medium">{t("googlePlay")}</p>
                  </div>
                </Link>
              </li>
            </ul>
          </div> */}
        </div>
        {/* <!-- footer menu end --> */}
      </div>

      <FooterBottom />
    </footer>
  );
};

export default Footer;
